/**
 * « Modifier » a deal in its sheet (PLAN-ENCRE-OUTILS O3): the form as typed, and the changes it
 * makes — only the fields that differ from the deal as read, so a save never writes back a value
 * someone (or Otto) changed meanwhile. Amounts accept « 1 200,50 ». Pure.
 */
import type { CRMOpportunity } from '../../types/crm';
import { dayOf, type OpportunityPatch } from './opportunitySheet';

export type ProductMode = 'none' | 'catalogue' | 'custom';

export interface OpportunityForm {
  name: string;
  amount: string;
  probability: string;
  close_date: string;
  target_close_date: string;
  revenue_type: CRMOpportunity['revenue_type'];
  one_time_amount: string;
  product: ProductMode;
  stripe_product_id: string;
  custom_product_name: string;
  custom_mrr: string;
  reason_lost: string;
}

type EditedDeal = Pick<
  CRMOpportunity,
  | 'name'
  | 'amount'
  | 'probability'
  | 'close_date'
  | 'target_close_date'
  | 'revenue_type'
  | 'one_time_amount'
  | 'stripe_product_id'
  | 'custom_product_name'
  | 'calculated_mrr'
  | 'status'
  | 'reason_lost'
>;

const text = (value: number | string | null): string => (value == null ? '' : String(value).replace('.', ','));

export function formOf(deal: EditedDeal): OpportunityForm {
  return {
    name: deal.name ?? '',
    amount: text(deal.amount),
    probability: text(deal.probability),
    // `close_date` is a timestamp: the field shows its day (a raw timestamp leaves it blank).
    close_date: dayOf(deal.close_date),
    target_close_date: dayOf(deal.target_close_date),
    revenue_type: deal.revenue_type,
    one_time_amount: text(deal.one_time_amount),
    product: deal.stripe_product_id ? 'catalogue' : deal.custom_product_name ? 'custom' : 'none',
    stripe_product_id: deal.stripe_product_id ?? '',
    custom_product_name: deal.custom_product_name ?? '',
    custom_mrr: deal.stripe_product_id ? '' : text(deal.calculated_mrr),
    reason_lost: deal.reason_lost ?? '',
  };
}

/** « 1 200,50 » → 1200.5; empty → null; anything else, or below zero → undefined (refused). */
export function parseAmount(value: string): number | null | undefined {
  const cleaned = value.replace(/[\s\u00a0\u202f€]/gu, '').replace(',', '.');
  if (cleaned === '') return null;
  if (!/^\d+(\.\d+)?$/u.test(cleaned)) return undefined;
  return Number(cleaned);
}

/** The changes the form makes to the deal, or what is wrong with it (said as is). */
export function editPatch(deal: EditedDeal, form: OpportunityForm): { patch: OpportunityPatch } | { error: string } {
  const patch: OpportunityPatch = {};
  const name = form.name.trim();
  if (!name) return { error: "L'opportunité a besoin d'un nom." };
  if (name !== deal.name) patch.name = name;

  const amount = parseAmount(form.amount);
  if (amount === undefined) return { error: 'Montant illisible : un nombre, comme 1 200 ou 1 200,50.' };
  if (amount !== deal.amount) patch.amount = amount;

  const probability = parseAmount(form.probability);
  if (probability === undefined || (probability !== null && (probability > 100 || !Number.isInteger(probability)))) {
    return { error: 'Probabilité : un nombre entier entre 0 et 100.' };
  }
  if (probability !== deal.probability) patch.probability = probability;

  // Compared as days: an untouched timestamp is not written back.
  if (form.close_date !== dayOf(deal.close_date)) patch.close_date = form.close_date || null;
  if (form.target_close_date !== dayOf(deal.target_close_date)) patch.target_close_date = form.target_close_date || null;

  if (form.revenue_type !== deal.revenue_type) patch.revenue_type = form.revenue_type;
  if (form.revenue_type !== 'recurring') {
    const oneTime = parseAmount(form.one_time_amount);
    if (oneTime === undefined) return { error: 'Montant ponctuel illisible : un nombre, comme 1 500.' };
    if (oneTime !== deal.one_time_amount) patch.one_time_amount = oneTime;
  }

  if (form.product === 'catalogue') {
    if (!form.stripe_product_id) return { error: 'Choisis le produit du catalogue, ou un autre type de produit.' };
    if (form.stripe_product_id !== deal.stripe_product_id || deal.custom_product_name) {
      // The database derives the MRR and ARR from the catalogue's price.
      Object.assign(patch, { stripe_product_id: form.stripe_product_id, custom_product_name: null });
    }
  } else if (form.product === 'custom') {
    const productName = form.custom_product_name.trim();
    if (!productName) return { error: 'Le produit personnalisé a besoin d’un nom.' };
    const mrr = parseAmount(form.custom_mrr);
    if (mrr === undefined) return { error: 'MRR illisible : un nombre, comme 720.' };
    if (deal.stripe_product_id || productName !== deal.custom_product_name || mrr !== deal.calculated_mrr) {
      // A product typed by hand keeps its MRR; the database sets its ARR (12 × MRR).
      Object.assign(patch, { stripe_product_id: null, custom_product_name: productName, calculated_mrr: mrr });
    }
  } else if (deal.stripe_product_id || deal.custom_product_name) {
    // No product: the database clears the MRR and ARR.
    Object.assign(patch, { stripe_product_id: null, custom_product_name: null });
  }

  if (deal.status === 'lost') {
    const reason = form.reason_lost.trim() || null;
    if (reason !== deal.reason_lost) patch.reason_lost = reason;
  }
  return { patch };
}
